<template>
  <div class="swimlane" :class="{ 'is-collapsed': collapsed }">
    <!-- header -->
    <div class="swimlane-header" @click="collapsed = !collapsed">
      <button
        class="swimlane-collapse-btn"
        type="button"
        :aria-label="collapsed ? $t('orders.details.kanban.expand') : $t('orders.details.kanban.collapse')">
        <IconChevronLeft v-if="!collapsed" :size="18" />
        <IconChevronRight v-else :size="18" />
      </button>
      <span class="swimlane-title text-truncate">{{ lane.name }}</span>
      <span class="swimlane-count badge">{{ lane.count }}</span>
    </div>

    <div v-if="!collapsed" ref="bodyRef" class="swimlane-body" :data-lane-key="lane.lane_key">
      <div v-if="isLoading" class="swimlane-empty text-muted">
        <div class="spinner-border spinner-border-sm" role="status"></div>
      </div>
      <div v-else-if="lane.candidates.length === 0" class="swimlane-empty swimlane-empty--lane text-muted">
        {{ $t('orders.details.kanban.empty_lane') }}
      </div>
      <template v-else>
        <CandidateCard
          v-for="candidate in lane.candidates"
          :key="candidate.candidate_entity_id"
          :data-candidate-id="candidate.candidate_entity_id"
          :candidate="candidate"
          :density="density" />
        <div v-if="lane.has_more" class="swimlane-empty text-muted">
          {{ $t('orders.details.kanban.has_more') }}
        </div>
      </template>
    </div>
  </div>
</template>
<script setup lang="ts">
import type { BoardLane, CardDensity } from '@/types/kanban';
import type { SortableEvent } from 'sortablejs';
import CandidateCard from '@/components/orders/kanban/CandidateCard.vue';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-vue';
import { onBeforeUnmount, ref, watch } from 'vue';
import Sortable from '@/utils/sortable';

export interface MoveEvent {
  candidateEntityIds: string[];
  toLaneKey: string;
}

// OS-native multi-select modifier: Cmd on macOS, Ctrl elsewhere
const isMac = /Mac/i.test(typeof navigator !== 'undefined' ? navigator.platform : '');
const MULTI_DRAG_KEY = isMac ? 'Meta' : 'Control';
const SELECTED_CLASS = 'card-selected';

withDefaults(
  defineProps<{
    lane: BoardLane;
    density?: CardDensity;
    isLoading?: boolean;
  }>(),
  { density: 'M' }
);

const emit = defineEmits<{
  move: [event: MoveEvent];
  'selection-change': [candidateEntityIds: string[]];
}>();

const collapsed = defineModel<boolean>('collapsed', { default: false });

/* ----------------------------- DRAG AND DROP ------------------------------ */
const bodyRef = ref<HTMLElement | null>(null);
let sortable: Sortable | null = null;

const destroySortable = () => {
  sortable?.destroy();
  sortable = null;
};

// report the full current selection (across all lanes) up to the board
const emitSelection = () => {
  const ids = [...document.querySelectorAll<HTMLElement>('.candidate-card.card-selected')]
    .map((el) => el.dataset.candidateId)
    .filter((id): id is string => Boolean(id));
  emit('selection-change', [...new Set(ids)]);
};

// (re)create the Sortable instance whenever the body mounts (it unmounts on collapse)
watch(bodyRef, (el) => {
  destroySortable();
  if (!el) return;

  sortable = Sortable.create(el, {
    group: 'candidates',
    draggable: '.candidate-card',
    multiDrag: true,
    selectedClass: SELECTED_CLASS,
    multiDragKey: MULTI_DRAG_KEY,
    sort: false,
    animation: 150,
    onSelect: emitSelection,
    onDeselect: emitSelection,
    onEnd: (event: SortableEvent) => {
      const items = event.items.length ? event.items : [event.item];
      const candidateEntityIds = items
        .map((item) => (item as HTMLElement).dataset.candidateId)
        .filter((id): id is string => Boolean(id));
      const toLaneKey = (event.to as HTMLElement | undefined)?.dataset.laneKey;

      if (candidateEntityIds.length && toLaneKey) {
        emit('move', { candidateEntityIds, toLaneKey });
      }
    },
  });
});

onBeforeUnmount(destroySortable);
</script>
<style scoped>
.swimlane {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-width: 200px;
  background: #fff;
  border-right: 1px solid #eaecf0;
}

.swimlane.is-collapsed {
  flex: 0 0 44px;
  min-width: 44px;
  width: 44px;
}

.swimlane-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  cursor: pointer;
  user-select: none;
  background: #f9fafb;
  border-bottom: 1px solid #eaecf0;
  position: sticky;
  top: 0;
  z-index: 1;
}

.swimlane.is-collapsed .swimlane-header {
  flex-direction: column;
  height: 100%;
  justify-content: flex-start;
}

.swimlane.is-collapsed .swimlane-title {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  flex: 0 0 auto;
  padding-bottom: 1rem;
}

.swimlane-collapse-btn {
  display: inline-flex;
  align-items: center;
  border: none;
  background: transparent;
  padding: 0;
  color: #475467;
}

.swimlane-title {
  flex: 1;
  font-weight: 600;
  font-size: 14px;
  color: #101828;
}

.swimlane-count {
  background: #eaecf0;
  color: #475467;
  border-radius: 16px;
  font-weight: 500;
}

.swimlane-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  flex: 1;
}

.swimlane-empty {
  padding: 12px;
  text-align: center;
  font-size: 13px;
}

/* while a card is dragged over an empty lane, hide the "no candidates" placeholder */
.swimlane-body:has(.candidate-card) .swimlane-empty--lane {
  display: none;
}
</style>
